import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';

import {
    GameCardCTA,
    GameNumber,
    Heading,
    HeadingTextWrapper,
    HeadingWrapper,
    Image,
    InnerCardContent,
    PrizeRowWrapper,
    PrizesValueLabel,
    PrizeValuePill,
    WrapperExternalLink,
    WrapperLink,
} from './styles';
import { Competition, Currency } from '@/client/common/structs';

import { formatUsd } from '@/utilities';
import { getMashedUrl } from '@/helpers';
import { FALLBACK_HEADER_IMG_SRC } from '@/helpers/dataSources/defaults/competitions';

import { getPrizeTotals, useGameStatusContent, useBreakpoints, useGetGameState } from '@/hooks';
import { useFantasyTracking, useLeaderboardCount } from '@/pages/FantasyWishlist/hooks';
import { GameTimer } from '@/pages/FantasyWishlist/components/Header/GameTimer';

const ALPHA_GAME_URL = 'https://fantasy.y.at/games/';

interface Props {
    competition: Competition;
    disableFadeIn?: boolean;
    isAlphaGame?: boolean;
}

const GameCard = ({ competition, disableFadeIn, isAlphaGame }: Props) => {
    const src = competition?.header_images ? competition.header_images[0] : '';
    const { t: translate } = useTranslation(['fantasy_wishlist']);
    const { isMobile } = useBreakpoints();
    const { ref: cardContainer, inView } = useInView({
        triggerOnce: true,
    });
    const { gameState } = useGetGameState(competition);
    const { gameCardCTA } = useGameStatusContent(gameState);
    const [disabled, setDisabled] = useState(false);

    const leaderboardCount = useLeaderboardCount(competition);

    useEffect(() => {
        setDisabled(gameState === 'upcoming');
    }, [gameState]);

    const { trackNavFantasyClick } = useFantasyTracking();

    function handleTrackingLink() {
        trackNavFantasyClick(competition.id, competition.slug, isAlphaGame ? 'all-games-card-alpha' : 'all-games-card');
    }

    const hasFadeIn = !disableFadeIn || isMobile;

    const mashedImageUrl = getMashedUrl({
        width: 600,
        url: src,
    });

    if (!competition?.slug) return null;

    const prizePoolMarkup = competition.prizes?.length && competition.prizes?.some((p) => p.reward?.length) && (
        <PrizeRowWrapper>
            <PrizesValueLabel>{translate('competition_prize_values')}</PrizesValueLabel>
            {competition.prizes?.map((prize, i) => {
                const { currency, value } = getPrizeTotals([prize]);
                let formattedTotal: string;
                if (currency === Currency.USD) {
                    formattedTotal = formatUsd(value, true);
                } else {
                    formattedTotal = `${currency} - ${value}`;
                }

                if (competition?.multigame) {
                    if (leaderboardCount === 0 || i > 0) return null;
                    formattedTotal = formatUsd(leaderboardCount, true);
                    return (
                        <PrizeValuePill key={`${competition.slug}-${prize.rank}-${i}`}>{formattedTotal}</PrizeValuePill>
                    );
                }

                return <PrizeValuePill key={`${competition.slug}-${prize.rank}-${i}`}>{formattedTotal}</PrizeValuePill>;
            })}
        </PrizeRowWrapper>
    );

    const innerContent = (
        <InnerCardContent
            ref={cardContainer}
            initial={{ opacity: hasFadeIn ? 0 : 1 }}
            animate={{ opacity: inView ? 1 : 0 }}
            transition={{ delay: hasFadeIn ? 0.3 : 0, duration: hasFadeIn ? 0.3 : 0 }}
        >
            <GameTimer providedGame={competition} compact />
            <Image src={(isAlphaGame ? src : mashedImageUrl) || FALLBACK_HEADER_IMG_SRC} loading="lazy" width={295} />
            <HeadingWrapper>
                <HeadingTextWrapper>
                    <GameNumber>{competition.game_label}</GameNumber>
                    <Heading>{competition.name}</Heading>
                </HeadingTextWrapper>
            </HeadingWrapper>
            {prizePoolMarkup}
            <GameCardCTA $disabled={disabled} $status={gameState}>
                {gameCardCTA}
            </GameCardCTA>
        </InnerCardContent>
    );

    return isAlphaGame ? (
        <WrapperExternalLink href={`${ALPHA_GAME_URL}${competition.slug}`} onClick={() => handleTrackingLink()}>
            {innerContent}
        </WrapperExternalLink>
    ) : (
        <WrapperLink
            // created using Link component, so it still triggers the react-router loader
            to={gameState !== 'open' ? `${competition.slug}/leaderboard` : `${competition.slug}`}
            $disabled={disabled} // TODO add future game state for game page
            onClick={() => handleTrackingLink()}
        >
            {innerContent}
        </WrapperLink>
    );
};

export default GameCard;
